import React, { useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

// components
import { Typography } from '../../components/Wrappers/Wrappers';
import Widget from '../../components/Widget/Widget';
import {
    Breadcrumb,
    FormFooter,
    CaseAttributes,
    CaseDesign,
    CaseMethod,
    FileAttachment,
    OrderConfirmation,
    InvoicingDelivery,
} from './components';

// context
import { useOrderDispatch, useOrderState, actions as orderActions } from '../../context/OrderContext';
import { usePracticeDispatch, usePracticeState, actions as practiceActions } from '../../context/PracticeContext';

// form helper
import {
    emptyOrder,
    invoicingRecipientAttributes,
    caseDesignAttributes,
    deliveryAddressAttributes,
    findMissingRequiredFields,
    defaultFormStepsTitle,
    hasMissingRequiredFields,
    formatOrderBeforeSubmission,
    impressionMethod,
    transferMethod,
} from './helpers/form';

import { otherOption } from './components/InvoicingDelivery';

const applyTreatmentTypeFromInitialOrder = (order) => {
    if (order.treatmentType.match('3 to 3')) {
        return '';
    }
    if (order.treatmentType.match('7 to 7')) {
        return '7 to 7';
    } else {
        return '5 to 5';
    }
};

const AddOrder = () => {
    const orderDispatch = useOrderDispatch();
    const orderState = useOrderState();

    const practiceDispatch = usePracticeDispatch();
    const practiceState = usePracticeState();

    const history = useHistory();
    const location = useLocation();

    const [newOrder, setNewOrder] = useState(emptyOrder());
    const [activeStep, setActiveStep] = useState(0);
    const [acceptTC, setAcceptTC] = useState(false);
    const [error, setError] = useState({});

    const allowFullArchTreatment = practiceState.currentPractice?.allowFullArchTreatment === 'Yes' ?? false;

    // ===== Hooks  =====

    // reset form and load the practices for selection
    useEffect(() => {
        orderActions.doNewForm()(orderDispatch);
        if (!practiceState.practices) practiceActions.doFetch()(practiceDispatch);
    }, []); // eslint-disable-line

    // find the order when ids are passed as query strings
    useEffect(() => {
        const queryString = new URLSearchParams(location.search);
        const practiceId = queryString.get('practiceId');
        const orderId = queryString.get('orderId');
        if (practiceId && orderId) {
            orderActions.doFind(practiceId, orderId)(orderDispatch);
        } else {
            orderActions.doNewForm()(orderDispatch);
        }
    }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

    // pre-load with current order when editing
    useEffect(() => {
        const currentOrder = orderState.currentOrder;
        if (currentOrder) {
            currentOrder.treatmentType = applyTreatmentTypeFromInitialOrder(currentOrder);
            // Convert patientDOB from full ISO date to YYYY-MM-DD format for date input
            if (currentOrder.patientDOB) {
                const date = new Date(currentOrder.patientDOB);
                currentOrder.patientDOB = date.toISOString().split('T')[0];
            }
            setNewOrder(currentOrder);
        } else {
            setNewOrder(emptyOrder());
        }
    }, [orderState.currentOrder]);

    useEffect(() => {
        if (newOrder.practiceId) {
            practiceActions.doFind(newOrder.practiceId)(practiceDispatch);
        }
    }, [newOrder.practiceId, practiceDispatch]);

    // ===== Navigation  =====

    const goNext = (step = 1) => setActiveStep((prevActiveStep) => prevActiveStep + step);

    const goBack = (step = 1) => setActiveStep((prevActiveStep) => prevActiveStep - step);

    const checkCaseAttributes = () => {
        const { practiceId, prescriberName, patientName, patientGender, patientDOB } = newOrder;
        const isValidPrescriberName = prescriberName.trim().length;
        const isValidPatientName = patientName.trim().length;

        // Validate date of birth age range (8-90 years old) - optional field
        let isValidDOB = true; // Default to true since it's optional
        if (patientDOB) {
            const birthDate = new Date(patientDOB);
            const today = new Date();
            const age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();

            // Adjust age if birthday hasn't occurred this year
            const adjustedAge =
                monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;

            isValidDOB = adjustedAge >= 8 && adjustedAge <= 90;
        }

        const allFieldsValid = practiceId && isValidPrescriberName && isValidPatientName && patientGender && isValidDOB;

        if (allFieldsValid) {
            return goNext();
        }

        setError({
            ...error,
            practiceId: !practiceId,
            prescriberName: !isValidPrescriberName,
            patientName: !isValidPatientName,
            patientGender: !patientGender,
            patientDOB: patientDOB && !isValidDOB, // Only show error if date is provided but invalid
        });
    };

    const checkCaseDesign = () => {
        const caseDesignForm = caseDesignAttributes(newOrder, allowFullArchTreatment);
        const requiredFieldErrors = findMissingRequiredFields(caseDesignForm, newOrder);

        // Check if instructions field exceeds 1,000 character limit
        const isValidInstructions =
            newOrder.instructions && newOrder.instructions.length > 0 && newOrder.instructions.length <= 1000;
        if (!isValidInstructions) {
            requiredFieldErrors.instructions = true;
        }

        setError({ ...error, ...requiredFieldErrors });
        if (!hasMissingRequiredFields(requiredFieldErrors)) goNext();
    };

    const checkMethod = () => {
        if (!newOrder.method) {
            toast.error('Please select an option');
        } else {
            goNext();
        }
    };

    const checkFileAttachment = () => {
        // Helper to check if photos have been uploaded
        const hasPhotos =
            orderState.photosUpload &&
            orderState.photosUpload.uploadedFiles &&
            orderState.photosUpload.uploadedFiles.length > 0;

        // For impression method, 3D files are not required - but photos are mandatory
        if (newOrder.method === impressionMethod) {
            if (!hasPhotos) {
                toast.error("Please upload the patient's photos");
                return;
            }
            goNext();
            return;
        }

        // For transfer method, 3D files are not required - but photos are mandatory
        if (newOrder.method === transferMethod) {
            if (!hasPhotos) {
                toast.error("Please upload the patient's photos");
                return;
            }
            goNext();
            return;
        }

        // For scan method, 3D files are mandatory
        const hasMaxillaryArch =
            orderState.maxillaryArchUpload &&
            orderState.maxillaryArchUpload.uploadedFiles &&
            orderState.maxillaryArchUpload.uploadedFiles.length > 0;
        const hasMandibularArch =
            orderState.mandibularArchUpload &&
            orderState.mandibularArchUpload.uploadedFiles &&
            orderState.mandibularArchUpload.uploadedFiles.length > 0;

        if (!hasMaxillaryArch) {
            toast.error('Please upload the upper arch file');
            return;
        }

        if (!hasMandibularArch) {
            toast.error('Please upload the lower arch file');
            return;
        }

        // Both mandatory 3D files are present, proceed to next step
        goNext();
    };

    const checkInvoicingAndDelivery = () => {
        let isValidInvoicing = true;
        if (newOrder.invoicingOption === otherOption) {
            const requiredFieldErrors = findMissingRequiredFields(invoicingRecipientAttributes, newOrder);
            isValidInvoicing = !hasMissingRequiredFields(requiredFieldErrors);
            setError({ ...error, ...requiredFieldErrors });
        }
        if (!isValidInvoicing) return;

        let isValidDelivery = true;
        if (newOrder.deliveryOption === otherOption) {
            const requiredFieldErrors = findMissingRequiredFields(deliveryAddressAttributes, newOrder);
            isValidDelivery = !hasMissingRequiredFields(requiredFieldErrors);
            setError({ ...error, ...requiredFieldErrors });
        }
        if (!isValidDelivery) return;

        if (!acceptTC) {
            toast.error('You must accept the Terms & Conditions');
        } else {
            doSubmit(newOrder, history);
            goNext();
        }
    };

    const checkOrderConfirmation = () => history.push('/app/dashboard');

    const formStepsValidation = [
        checkCaseAttributes,
        checkCaseDesign,
        checkMethod,
        checkFileAttachment,
        checkInvoicingAndDelivery,
        checkOrderConfirmation,
    ];

    // ===== Handlers  =====

    const handleTextFieldChange = (e) => {
        if (e.target.required) setError({ ...error, [e.target.name]: !e.target.value });
        // prevent the search field from updating the newOrder
        if (e.target.name === 'practiceSearch') return;

        const updates = { [e.target.name]: e.target.value };
        if (e.target.name === 'arches') {
            if (e.target.value === 'Upper only') updates.lowerMidline = '';
            else if (e.target.value === 'Lower only') updates.upperMidline = '';
        }
        setNewOrder({ ...newOrder, ...updates });
    };

    const handleMethodClicked = (method) => (_) => setNewOrder({ ...newOrder, method });

    const handleNext = async () => formStepsValidation[activeStep]();

    const handleBack = () => goBack();

    const doSubmit = async (formValues) => {
        const data = formatOrderBeforeSubmission(
            formValues,
            practiceState.currentPractice,
            orderState.photosUpload,
            orderState.maxillaryArchUpload,
            orderState.mandibularArchUpload
        );
        data.territory = practiceState.currentPractice.territory;
        if (data.orderId) {
            // finalize an order that was waiting for impression
            await orderActions.doFinalizeNewOrder(data.orderId, data)(orderDispatch);
        } else {
            // create a new order
            await orderActions.doCreateNewOrder(data)(orderDispatch);
        }
    };

    const formStepsComponent = [
        <CaseAttributes newOrder={newOrder} formErrors={error} onTextFieldChange={handleTextFieldChange} />,
        <CaseDesign
            newOrder={newOrder}
            allowFullArchTreatment={allowFullArchTreatment}
            formErrors={error}
            onTextFieldChange={handleTextFieldChange}
        />,
        <CaseMethod newOrder={newOrder} methodClicked={handleMethodClicked} />,
        <FileAttachment newOrder={newOrder} />,
        <InvoicingDelivery
            practice={practiceState.currentPractice}
            newOrder={newOrder}
            formErrors={error}
            onTextFieldChange={handleTextFieldChange}
            termsAccepted={acceptTC}
            termsClicked={(clicked) => setAcceptTC(clicked)}
        />,
        <OrderConfirmation newOrder={newOrder} />,
    ];

    return (
        <Grid container spacing={3}>
            <Breadcrumb activeStep={activeStep} stepsTitle={defaultFormStepsTitle} />
            <Grid item xs={12}>
                <Widget>
                    <Grid item justifyContent="center" container>
                        <Box display="flex" flexDirection="column" width={600}>
                            <Typography variant="h5" weight="medium" style={{ marginBottom: 30 }}>
                                {defaultFormStepsTitle[activeStep]}
                            </Typography>
                            {formStepsComponent[activeStep]}
                            <FormFooter
                                activeStep={activeStep}
                                isRefinement={false}
                                onClickNext={handleNext}
                                onClickBack={handleBack}
                            />
                        </Box>
                    </Grid>
                </Widget>
            </Grid>
        </Grid>
    );
};

export default AddOrder;
